/** Sponsor placement helpers + URL hydration (pool comes from API). */

import { rewriteLocalMediaUrl } from '../constants/config';

export const CANVAS_WIDTH = 1080;
export const DEFAULT_SPONSOR_WIDTH_RATIO = 0.2;
export const MIN_SPONSOR_WIDTH = 48;
export const MAX_SPONSOR_WIDTH_RATIO = 0.55;
export const SNAP_THRESHOLD = 10;
export const SNAP_GRID = 20;

/** Vertical anchor for schemes / dynamic-height pictures. */
export const SPONSOR_ANCHOR_TOP = 'top';
export const SPONSOR_ANCHOR_BOTTOM = 'bottom';

export const normalizeSponsorAnchorY = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'bottom' || raw === 'bottom-left' || raw === 'bl' || raw === 'b') {
    return SPONSOR_ANCHOR_BOTTOM;
  }
  return SPONSOR_ANCHOR_TOP;
};

/** CSS-like bottom inset: distance from canvas bottom to the bottom edge of the box. */
export const bottomOffsetFromTopY = (y, height, canvasHeight) => (
  Math.max(0, (Number(canvasHeight) || 0) - (Number(y) || 0) - (Number(height) || 0))
);

export const topYFromBottomOffset = (bottomOffset, height, canvasHeight) => (
  Math.max(0, (Number(canvasHeight) || 0) - (Number(bottomOffset) || 0) - (Number(height) || 0))
);

/** Center of the logo is in the lower half of the canvas → treat as bottom-anchored. */
export const isSponsorInLowerHalf = (item, canvasHeight) => {
  const height = Number(canvasHeight) || 0;
  if (!height || !item) return false;
  const boxH = Number(item.height) || 0;
  const centerY = (Number(item.y) || 0) + boxH / 2;
  return centerY >= height / 2;
};

let sponsorIdSeq = 1;
export const nextSponsorInstanceId = () => `sp_${Date.now()}_${sponsorIdSeq++}`;

export const resolveMediaUrl = (value) => rewriteLocalMediaUrl(value);

export const normalizePoolItem = (item = {}) => {
  const key = String(item.key || item.id || '').trim();
  const name = String(item.name || key || '').trim() || key;
  const image = String(item.image || '').trim();
  const src = resolveMediaUrl(item.src || item.preview || image);
  return {
    id: item.id != null ? item.id : key,
    key,
    name,
    image,
    src,
    preview: resolveMediaUrl(item.preview || item.src || image) || src,
  };
};

export const loadImageNaturalSize = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve({
    width: img.naturalWidth || img.width || 1,
    height: img.naturalHeight || img.height || 1,
  });
  img.onerror = () => reject(new Error(`Failed to load ${src}`));
  img.crossOrigin = 'anonymous';
  img.src = src;
});

export const createSponsorPlacement = async ({
  poolItem,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = 1400,
  offsetIndex = 0,
  previous = null,
}) => {
  const src = resolveMediaUrl(poolItem.src || poolItem.preview || poolItem.image);
  const natural = await loadImageNaturalSize(src);
  const aspect = Math.max(0.05, natural.width / Math.max(1, natural.height));
  const maxW = canvasWidth * MAX_SPONSOR_WIDTH_RATIO;
  const maxH = canvasHeight * 0.35;
  const gap = 16;

  let width;
  let height;
  let x;
  let y;

  if (previous && previous.height > 0) {
    height = Math.min(maxH, Math.max(MIN_SPONSOR_WIDTH / aspect, previous.height));
    width = height * aspect;
    if (width > maxW) {
      width = maxW;
      height = width / aspect;
    }
    x = previous.x + previous.width + gap;
    y = previous.y;
    if (x + width > canvasWidth) {
      x = Math.max(0, Math.min(canvasWidth - width, previous.x));
      y = Math.min(canvasHeight - height, previous.y + previous.height + gap);
    }
  } else {
    width = Math.min(maxW, Math.max(MIN_SPONSOR_WIDTH, canvasWidth * DEFAULT_SPONSOR_WIDTH_RATIO));
    height = width / aspect;
    if (height > maxH) {
      height = maxH;
      width = height * aspect;
    }
    const stagger = (offsetIndex % 5) * 28;
    x = Math.max(0, Math.min(canvasWidth - width, canvasWidth * 0.08 + stagger));
    y = Math.max(0, Math.min(canvasHeight - height, canvasHeight * 0.06 + stagger));
  }

  x = Math.max(0, Math.min(canvasWidth - width, x));
  y = Math.max(0, Math.min(canvasHeight - height, y));

  return {
    id: nextSponsorInstanceId(),
    poolKey: poolItem.key || poolItem.poolKey || '',
    poolId: Number.isFinite(Number(poolItem.id)) ? Number(poolItem.id) : null,
    src,
    image: poolItem.image || '',
    name: poolItem.name || poolItem.key || 'sponsor',
    aspect,
    anchorY: SPONSOR_ANCHOR_TOP,
    bottom: null,
    x,
    y,
    width,
    height,
  };
};

/** Hydrate stored/server placement into editor model (absolute top-left for drag). */
export const hydrateSponsorPlacement = (item = {}, index = 0, canvasHeight = 0) => {
  const width = Math.max(1, Number(item.width) || 1);
  const height = Math.max(1, Number(item.height) || 1);
  const src = resolveMediaUrl(item.src || item.preview || item.image || item.url);
  const instanceId = typeof item.id === 'string' && String(item.id).startsWith('sp_')
    ? item.id
    : nextSponsorInstanceId();
  const hasExplicitBottom = item.bottom != null || item.bottomOffset != null;
  let anchorY = normalizeSponsorAnchorY(
    item.anchorY || item.anchor || (hasExplicitBottom ? SPONSOR_ANCHOR_BOTTOM : SPONSOR_ANCHOR_TOP)
  );
  let y = Number(item.y) || 0;
  let bottom = null;
  if (anchorY === SPONSOR_ANCHOR_BOTTOM) {
    // API contract: when anchorY=bottom, `y` is CSS-like bottom inset (unless `bottom` given).
    bottom = hasExplicitBottom
      ? Number(item.bottom != null ? item.bottom : item.bottomOffset)
      : y;
    y = canvasHeight > 0 ? topYFromBottomOffset(bottom, height, canvasHeight) : 0;
  } else if (canvasHeight > 0 && isSponsorInLowerHalf({ y, height }, canvasHeight)) {
    // Legacy absolute coords in the lower half → pin from bottom automatically.
    anchorY = SPONSOR_ANCHOR_BOTTOM;
    bottom = bottomOffsetFromTopY(y, height, canvasHeight);
  }
  return {
    id: instanceId,
    poolKey: item.poolKey || item.key || '',
    poolId: item.poolId != null && Number.isFinite(Number(item.poolId))
      ? Number(item.poolId)
      : null,
    src,
    image: item.image || '',
    name: item.name || item.poolKey || item.key || `sponsor_${index + 1}`,
    aspect: width / height,
    anchorY,
    bottom,
    x: Number(item.x) || 0,
    y,
    width,
    height,
  };
};

/** Keep bottom-anchored logos stuck to the bottom when canvas height changes. */
export const resolveSponsorLayout = (
  item,
  canvasHeight,
  previousCanvasHeight = 0,
  canvasWidth = CANVAS_WIDTH
) => {
  if (!canvasHeight) return item;
  const width = Number(canvasWidth) > 0 ? Number(canvasWidth) : CANVAS_WIDTH;
  const prevH = Number(previousCanvasHeight) || 0;
  const refH = prevH > 0 ? prevH : canvasHeight;
  const useBottom = isSponsorInLowerHalf(item, refH)
    || item.bottom != null
    || normalizeSponsorAnchorY(item.anchorY) === SPONSOR_ANCHOR_BOTTOM;

  if (useBottom) {
    const bottom = item.bottom != null
      ? Number(item.bottom)
      : bottomOffsetFromTopY(item.y, item.height, refH);
    const next = clampSponsorRect({
      ...item,
      y: topYFromBottomOffset(bottom, item.height, canvasHeight),
    }, width, canvasHeight);
    return {
      ...next,
      anchorY: SPONSOR_ANCHOR_BOTTOM,
      bottom: bottomOffsetFromTopY(next.y, next.height, canvasHeight),
    };
  }
  const next = clampSponsorRect(item, width, canvasHeight);
  return { ...next, anchorY: SPONSOR_ANCHOR_TOP, bottom: null };
};

/** Sync anchor from current position: lower half → bottom, else top. */
export const syncSponsorBottomOffset = (item, canvasHeight) => {
  if (!item || !canvasHeight) return item;
  if (!isSponsorInLowerHalf(item, canvasHeight)) {
    return { ...item, anchorY: SPONSOR_ANCHOR_TOP, bottom: null };
  }
  return {
    ...item,
    anchorY: SPONSOR_ANCHOR_BOTTOM,
    bottom: bottomOffsetFromTopY(item.y, item.height, canvasHeight),
  };
};

/** API body item: poolKey|poolId + coords only (no src/base64). */
export const buildSponsorsApiPayload = (sponsors = [], canvasHeight = 0) => (
  (sponsors || []).map((item) => {
    const poolKey = String(item.poolKey || item.key || '').trim();
    const poolId = item.poolId != null && item.poolId !== ''
      ? item.poolId
      : (Number.isFinite(Number(item.id)) && !String(item.id).startsWith('sp_')
        ? Number(item.id)
        : null);
    const width = Math.round(Number(item.width) || 0);
    const height = Math.round(Number(item.height) || 0);
    const useBottom = canvasHeight > 0
      ? isSponsorInLowerHalf(item, canvasHeight)
      : normalizeSponsorAnchorY(item.anchorY) === SPONSOR_ANCHOR_BOTTOM;
    const payload = {
      x: Math.round(Number(item.x) || 0),
      width,
      height,
    };
    if (useBottom) {
      const bottom = canvasHeight
        ? bottomOffsetFromTopY(item.y, item.height, canvasHeight)
        : (item.bottom != null ? Number(item.bottom) : Number(item.y) || 0);
      payload.anchorY = SPONSOR_ANCHOR_BOTTOM;
      // `y` = inset from canvas bottom to the bottom edge of the logo (CSS `bottom`).
      payload.y = Math.round(bottom);
      payload.bottom = Math.round(bottom);
    } else {
      payload.y = Math.round(Number(item.y) || 0);
    }
    if (poolKey) payload.poolKey = poolKey;
    else if (poolId != null) payload.poolId = poolId;
    return payload;
  }).filter((item) => (
    item.width > 0
    && item.height > 0
    && (item.poolKey || item.poolId != null)
  ))
);

const snapValue = (value, targets, threshold = SNAP_THRESHOLD) => {
  let best = value;
  let bestDist = threshold + 1;
  let guide = null;
  targets.forEach((target) => {
    const dist = Math.abs(value - target);
    if (dist <= threshold && dist < bestDist) {
      bestDist = dist;
      best = target;
      guide = target;
    }
  });
  return { value: best, guide };
};

export const snapSponsorRect = (rect, canvasWidth, canvasHeight, others = [], opts = {}) => {
  const threshold = opts.threshold ?? SNAP_THRESHOLD;
  const grid = opts.grid ?? SNAP_GRID;
  const { x, y, width, height } = rect;

  const xTargets = [
    0,
    canvasWidth / 2 - width / 2,
    canvasWidth - width,
    ...Array.from({ length: Math.floor(canvasWidth / grid) + 1 }, (_, i) => i * grid),
  ];
  const yTargets = [
    0,
    canvasHeight / 2 - height / 2,
    canvasHeight - height,
    ...Array.from({ length: Math.floor(canvasHeight / grid) + 1 }, (_, i) => i * grid),
  ];

  const vGuides = [0, canvasWidth / 2, canvasWidth];
  const hGuides = [0, canvasHeight / 2, canvasHeight];

  others.forEach((item) => {
    if (!item || item.id === rect.id) return;
    xTargets.push(item.x, item.x + item.width - width, item.x + item.width / 2 - width / 2);
    yTargets.push(item.y, item.y + item.height - height, item.y + item.height / 2 - height / 2);
    vGuides.push(item.x, item.x + item.width / 2, item.x + item.width);
    hGuides.push(item.y, item.y + item.height / 2, item.y + item.height);
  });

  const snappedX = snapValue(x, xTargets, threshold);
  const snappedY = snapValue(y, yTargets, threshold);

  const leftGuide = snapValue(snappedX.value, vGuides, threshold);
  const rightGuide = snapValue(snappedX.value + width, vGuides, threshold);
  const topGuide = snapValue(snappedY.value, hGuides, threshold);
  const bottomGuide = snapValue(snappedY.value + height, hGuides, threshold);
  const cxGuide = snapValue(snappedX.value + width / 2, vGuides, threshold);
  const cyGuide = snapValue(snappedY.value + height / 2, hGuides, threshold);

  const guides = { vertical: [], horizontal: [] };

  if (Math.abs(snappedX.value - leftGuide.value) < 0.01 && leftGuide.guide != null) {
    guides.vertical.push(leftGuide.guide);
  } else if (Math.abs((snappedX.value + width) - rightGuide.value) < 0.01 && rightGuide.guide != null) {
    guides.vertical.push(rightGuide.guide);
  } else if (Math.abs((snappedX.value + width / 2) - cxGuide.value) < 0.01 && cxGuide.guide != null) {
    guides.vertical.push(cxGuide.guide);
  }

  if (Math.abs(snappedY.value - topGuide.value) < 0.01 && topGuide.guide != null) {
    guides.horizontal.push(topGuide.guide);
  } else if (Math.abs((snappedY.value + height) - bottomGuide.value) < 0.01 && bottomGuide.guide != null) {
    guides.horizontal.push(bottomGuide.guide);
  } else if (Math.abs((snappedY.value + height / 2) - cyGuide.value) < 0.01 && cyGuide.guide != null) {
    guides.horizontal.push(cyGuide.guide);
  }

  return {
    x: snappedX.value,
    y: snappedY.value,
    width,
    height,
    guides,
  };
};

export const clampSponsorRect = (rect, canvasWidth, canvasHeight) => {
  const aspect = rect.aspect || (rect.width / Math.max(1, rect.height));
  const maxW = canvasWidth * MAX_SPONSOR_WIDTH_RATIO;
  let width = Math.min(maxW, Math.max(MIN_SPONSOR_WIDTH, rect.width));
  let height = width / aspect;
  if (height > canvasHeight) {
    height = canvasHeight;
    width = height * aspect;
  }
  const x = Math.max(0, Math.min(canvasWidth - width, rect.x));
  const y = Math.max(0, Math.min(canvasHeight - height, rect.y));
  return { ...rect, x, y, width, height, aspect };
};

export const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'table.png';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};
